import { useEffect, useRef, useState } from "react"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

const MAX_WIDTH_BEFORE_TEXT_TRUNCATE_IN_PIXEL = 0.5

const isOverflowActive = (e: HTMLElement) => {
  return e.getBoundingClientRect().width + MAX_WIDTH_BEFORE_TEXT_TRUNCATE_IN_PIXEL <= e.scrollWidth
}

type TextWithTooltipProps = React.HTMLAttributes<HTMLDivElement> & {
  tooltipContentClassName?: string
}

const TextWithTooltip = ({ className, tooltipContentClassName, children, ...otherProps }: TextWithTooltipProps) => {
  const [isOverflow, setIsOverflow] = useState(false)
  const textRef = useRef<HTMLSpanElement>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    if (!textRef.current) {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      if (textRef.current && isOverflowActive(textRef.current)) {
        setIsOverflow(true)
      } else {
        setIsOverflow(false)
      }
    })

    resizeObserver.observe(textRef.current)
    resizeObserverRef.current = resizeObserver
  })

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect()
    }
  }, [])

  return (
    <TooltipCustom>
      <TooltipTrigger asChild>
        <div className={cn("flex items-center", className)} {...otherProps}>
          <span className="truncate" ref={textRef} data-testid="inner-text">
            {children}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="bottom" align="start" className={cn(!isOverflow && "hidden", tooltipContentClassName)}>
          {children}
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}

export { TextWithTooltip }
